import React, {useEffect, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import Util from '../../forecast-app/shared/util/util';
import {PROJECT_STATUS} from '../../constants';
import * as tracking from '../../tracking';
import ProjectPage from '../../components/new-ui/project/project-budget-v2/project_page';
import RatesAndInternalCostPage from '../../components/new-ui/project/project-budget-v2/rates_and_internal_cost_page';
import ExpensesPage from '../../components/new-ui/project/project-budget-v2/expenses_page';
import BudgetWarningTab from '../../components/new-ui/project/project-budget-v2/components/budget_warning_tab';
import {TutorialVideo} from '../../components/new-ui/video_modal';
import TrackPersonEventMutation from '../../mutations/track_person_event_mutation';
import {dispatch, EVENT_ID} from '../event_manager';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import HelpCenterIcon from '../../components/new-ui/help_center_icon';

const projectBudgetV2 = props => {
	// available tabs at the top of the page
	const hasFinancials = Util.hasFinance(props.viewer.company.modules);
	const hasBaseline = Util.hasBaselineModule(props.viewer.company.modules) && props.viewer.project.useBaseline;
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();
	const useThirdLevelNav = hasFeatureFlag('third_level_nav', props.viewer.availableFeatureFlags);
	const tabs = hasFinancials
		? hasLimitedFinancialAccess
			? {
					PROJECT: 'project',
					EXPENSES: 'expenses',
			  }
			: {
					PROJECT: 'project',
					RATES_AND_INTERNAL_COST: 'rates_&_internal_cost',
					EXPENSES: 'expenses',
			  }
		: {
				PROJECT: 'project',
		  };

	// initialize state for the selected tab. Set default selectedTab to be tabs.PROJECT
	const [selectedTab, setSelectedTab] = useState((hasFinancials && localStorage.getItem('project-budget-v2-last-selected-tab')) || tabs.PROJECT);

	// similar to componentWillMount/ componentDidMount if passing empty array as the second argument
	useEffect(() => {
		Util.CommitMutation(TrackPersonEventMutation, {
			name: 'project-budget',
		});
		const path = props.history.location.pathname;
		if (!Util.AuthorizeViewerAccess('project-budget')) {
			if (Util.pathIncludesTask(path)) {
				const cardPath = path.substring(Util.indexOfTask(path), path.length);
				props.history.push('/project/P-' + props.viewer.project.companyProjectId + '/workflow/' + cardPath);
			} else {
				// if user doesnt have access rights to view this page redirect to no access page
				props.history.push('/not-authorized');
				Util.localStorageSetItem('project-section-last-viewed', 'workflow');
			}
		}

		// copied from coponentdidMount
		const name = props.viewer.project.name !== null && props.viewer.project.name !== '' ? props.viewer.project.name : 'P-' + props.viewer.project.companyProjectId;
		document.title = 'Budget - ' + name + ' - Forecast';

		dispatch(EVENT_ID.PAGE_LOADED, 'project_budget');
	}, []);

	const renderSelectedTab = () => {
		Util.localStorageSetItem('project-budget-v2-last-selected-tab', selectedTab);

		switch (selectedTab) {
			case tabs.PROJECT:
				return <ProjectPage viewer={props.viewer} retry={props.retry} hasFinancials={hasFinancials} hasBaseline={hasBaseline} />;
			case tabs.RATES_AND_INTERNAL_COST:
				return <RatesAndInternalCostPage project={props.viewer.project} company={props.viewer.company} relay={props.relay} retry={props.retry} availableFeatureFlags={props.viewer.availableFeatureFlags} />;
			case tabs.EXPENSES:
				return <ExpensesPage project={props.viewer.project} company={props.viewer.company} useThirdLevelNav={useThirdLevelNav} retry={props.retry} availableFeatureFlags={props.viewer.availableFeatureFlags} />;
			default:
				return null;
		}
	};

	const changeTab = tab => {
		setSelectedTab(tab);

		tracking.trackElementClicked(tab);

		// refetch data when going to the project tab to prevent the need refresh if something on the others tabs have updated.
		if (tab === tabs.PROJECT) props.retry();
	};

	const getHeaderTitle = () => {
		const content = [];
		content.push(<HelpCenterIcon key={'help-center-icon'} href={'https://help.forecast.it/project-budget'} />);

		if (props.viewer.project.status !== PROJECT_STATUS.DONE && props.viewer.project.status !== PROJECT_STATUS.HALTED) {
			content.push(
				<div className="warning-button">
					<BudgetWarningTab
						setSelectedTab={tab => {
							setSelectedTab(tab);
						}}
						hasFinancials={hasFinancials}
						project={props.viewer.project}
						tabs={tabs}
						viewer={props.viewer}
						relay={props.relay}
						retry={props.retry}
					/>
				</div>
			);
		}
		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={props.intl.formatMessage({id: 'common.budget'})} content={content} />
			</TopHeaderBarWrapper>
		);
	};

	const allTabs = Object.values(tabs); //[tabs.PROJECT, tabs.RATES_AND_INTERNAL_COST, tabs.EXPENSES];

	const {viewer} = props;
	const {
		project,
		company: {allPersons},
		actualPersonId,
	} = viewer;
	const {status} = project;

	const isCypressUser = !!allPersons.edges.find(person => person.node.id === actualPersonId && person.node.email.endsWith('@forecastit.dk'));

	if (!Util.AuthorizeViewerAccess('project-budget')) {
		return <div></div>;
	}

	return (
		<div className="budget-v2-wrapper" data-cy={'budget-page'}>
			{props.children} {/* renders the task modal if open */}
			<TutorialVideo videoId="budget" viewerId={viewer.id} videosShown={isCypressUser ? ['budget'] : viewer.videosShown} youtubeId="mduWThqownM" />
			{useThirdLevelNav ? getHeaderTitle() : null}
			<div className={'tabs' + (useThirdLevelNav ? ' third-level' : '')} data-userpilot="budget-tabs">
				{allTabs.map(tab => (
					<div key={tab} className={'tab' + (selectedTab === tab ? ' active' : '')} onClick={() => changeTab(tab)} data-cy={tab.split('_')[0]} data-userpilot={`${tab}_tab`}>
						{tab.replace(/_/g, ' ')}
					</div>
				))}
				{useThirdLevelNav || status === PROJECT_STATUS.DONE || status === PROJECT_STATUS.HALTED || (
					<BudgetWarningTab
						setSelectedTab={tab => {
							setSelectedTab(tab);
						}}
						hasFinancials={hasFinancials}
						project={props.viewer.project}
						tabs={tabs}
						viewer={props.viewer}
						relay={props.relay}
						retry={props.retry}
					/>
				)}
			</div>
			{renderSelectedTab()}
		</div>
	);
};

const projectBudgetV2Query = graphql`
	query projectBudgetV2_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "project_budget_v2", projectId: $projectId)
			project(id: $projectId) {
				id
			}
			...projectBudgetV2_viewer @arguments(projectId: $projectId)
		}
	}
`;

const project = graphql`
	fragment projectBudgetV2_project on ProjectType {
		id
		budgetType
		budget
		defaultPeriodBudgetType
		defaultPeriodPriceAmount
		companyProjectId
		name
		estimationUnit
		minutesPerEstimationPoint
		estimationUnit
		reportedBillablePrice(newBudget: true)
		currentCost
		forecastBillablePrice(newBudget: true)
		financialServiceUnInvoicedTotal
		originalForecastBillableHoursPrice
		forecastBillableHoursPrice
		forecastBillablePriceScopeBased
		forecastCost(newBudget: true)
		remainingCost(newBudget: true)
		reported
		forecastTotal
		remaining
		projectEndDay
		projectEndMonth
		projectEndYear
		projectStartDay
		projectStartMonth
		projectStartYear
		projectFirstDateYear
		projectFirstDateMonth
		projectFirstDateDay
		projectLastDateYear
		projectLastDateMonth
		projectLastDateDay
		billable
		taskCostCalculationType
		unassignedTaskHourlyCost
		status
		baselineCost
		baselineProfit
		baselineTargetMinutes
		useBaseline
		completion
		timeRegBasedPlusDoneScopeCompletion
		valueBasedProgress
		financialDataAvailability
		financialNumbers {
			totalRateValue
			budgetVsTotalRateValue
		}
		company {
			roles(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
			modules {
				moduleType
			}
			expenseCategories(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
		}
		xeroInvoices {
			id
			amountTotal
			amountNoTax
			amountPaid
		}
		registeredMinutesPerDay(newBudget: true) {
			date
			value
		}
		remainingMinutesPerDay(newBudget: true) {
			date
			value
		}
		plannedMinutesPerDay(newBudget: true) {
			date
			value
		}
		costPlannedPerDay(newBudget: true) {
			date
			value
		}
		pricePlannedPerDay(newBudget: true) {
			date
			value
		}
		costRemainingPerDay(newBudget: true) {
			date
			value
		}
		priceRemainingPerDay(newBudget: true) {
			date
			value
		}
		costActualPerDay(newBudget: true) {
			date
			value
		}
		priceActualPerDay(newBudget: true) {
			date
			value
		}
		costPlannedWorkPerDay(newBudget: true) {
			date
			value
		}
		pricePlannedWorkPerDay(newBudget: true) {
			date
			value
		}
		costRemainingWorkPerDay(newBudget: true) {
			date
			value
		}
		priceRemainingWorkPerDay(newBudget: true) {
			date
			value
		}
		costActualWorkPerDay(newBudget: true) {
			date
			value
		}
		priceActualWorkPerDay(newBudget: true) {
			date
			value
		}
		pricePlannedExpensesPerDay(newBudget: true) {
			date
			value
		}
		costPlannedExpensesPerDay(newBudget: true) {
			date
			value
		}
		priceActualExpensesPerDay(newBudget: true) {
			date
			value
		}
		costActualExpensesPerDay(newBudget: true) {
			date
			value
		}
		priceRemainingExpensesPerDay(newBudget: true) {
			date
			value
		}
		costRemainingExpensesPerDay(newBudget: true) {
			date
			value
		}
		invoicedPerDay(newBudget: true) {
			date
			value
		}
		totalPaymentsPerDay(newBudget: true) {
			date
			value
		}
		baselinePricePerDay {
			date
			value
		}
		baselineCostPerDay {
			date
			value
		}
		baselineProfitPerDay {
			date
			value
		}
		baselineMinutesPerDay {
			date
			value
		}
		baselineUnplannedPricePerDay {
			date
			value
		}
		baselineUnplannedMinutesPerDay {
			date
			value
		}
		breakdownForecastMinutesPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownRemainingMinutesPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownActualMinutesPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownCostForecastPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownCostRemainingPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownCostActualPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownPricePlannedPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownPriceRemainingPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownPriceActualPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownCostForecastWorkPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownCostRemainingWorkPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownCostActualWorkPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownPricePlannedWorkPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownPriceRemainingWorkPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownPriceActualWorkPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownBaselinePricePerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownBaselineCostPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownBaselineProfitPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownBaselineMinutesPerDay {
			milestoneId
			roleBreakdown {
				roleId
				breakdown {
					date
					value
				}
			}
		}
		breakdownBaselineExpensesPricePerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		breakdownBaselineExpensesCostPerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		breakdownExpensesPlannedPricePerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		breakdownExpensesActualsPricePerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		breakdownExpensesRemainingPricePerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		breakdownExpensesPlannedCostPerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		breakdownExpensesActualsCostPerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		breakdownExpensesRemainingCostPerDay {
			milestoneId
			expenseCategoryBreakdown {
				expenseCategoryId
				breakdown {
					date
					value
				}
			}
		}
		warnings {
			id
			year
			month
			day
			tasks(first: 10000) {
				edges {
					node {
						id
						name
						startDay
						startMonth
						startYear
						deadlineDay
						deadlineMonth
						deadlineYear
						estimateLow
						estimateHigh
						estimateForecast
					}
				}
			}
			personIds
			expenseItems(first: 10000) {
				edges {
					node {
						id
						name
						expenseYear
						expenseMonth
						expenseDay
					}
				}
			}
			timeRegistrations(first: 10000) {
				edges {
					node {
						id
						year
						month
						day
						minutesRegistered
						person {
							id
							fullName
							profilePictureId
							profilePictureDefaultId
						}
						task {
							name
						}
					}
				}
			}
		}
		tasks(first: 10000) {
			edges {
				node {
					id
					name
					role {
						id
					}
					estimateLow
					estimateHigh
					estimateForecast
					assignedPersons {
						id
					}
				}
			}
		}
		rateCard {
			id
			currency
			name
			defaultRate
			currency
			parentRateCardId
			rates(first: 10000) {
				edges {
					node {
						id
						rate
						defaultRate
						startDate
						role {
							id
							name
						}
					}
				}
			}
		}
		phases(first: 10000) {
			edges {
				node {
					id
					name
					startDay
					startMonth
					startYear
					deadlineDay
					deadlineMonth
					deadlineYear
				}
			}
		}
		fixedPriceLocks(first: 1000) @connection(key: "Project_fixedPriceLocks", filters: []) {
			edges {
				node {
					id
					startDate
					endDate
					actualRevenueProjectCurrency
					progress
				}
			}
		}
		expenseItems(first: 1000) @connection(key: "Project_expenseItems") {
			edges {
				node {
					id
					category {
						id
						name
					}
					phase {
						id
						name
					}
					name
					approved
					expenseYear
					expenseMonth
					expenseDay
					billable
					price
					cost
					quantity
					name
					planned
					createdAt
					invoiced
					person {
						id
						fullName
						profilePictureId
						profilePictureDefaultId
					}
				}
			}
		}
		projectPersons(first: 1000) {
			edges {
				node {
					id
					person {
						id
						isViewer
						fullName
						initials
						profilePictureId
						profilePictureDefaultId
						active
						userType
						permissions
						costPeriods(first: 10000) {
							edges {
								node {
									id
									startDay
									startMonth
									startYear
									cost
								}
							}
						}
					}
					role {
						id
						name
					}
				}
			}
		}
		invoices(first: 100000000) {
			edges {
				node {
					status
				}
			}
		}
	}
`;

export {project};

export {projectBudgetV2Query};

export default injectIntl(
	withRouter(
		createFragmentContainer(projectBudgetV2, {
			viewer: graphql`
				fragment projectBudgetV2_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
					id
					actualPersonId
					language
					videosShown(videoIds: ["budget"])
					availableFeatureFlags {
						key
					}
					permissions
					company {
						...expensesPage_company
						...ratesAndInternalCostPage_company
						id
						currency
						xeroEnabled
						newXeroEnabled
						modules {
							moduleType
						}
						exchangeRates(first: 10000) {
							edges {
								node {
									id
									currency
									rate
								}
							}
						}
						allPersons(first: 10000, onlyActive: true) {
							edges {
								node {
									id
									email
								}
							}
						}
						rateCards(first: 10000) {
							edges {
								...RateCardDropdown_rateCards
								node {
									id
									name
									defaultRate
									currency
									parentRateCardId
									rates(first: 10000) {
										edges {
											node {
												id
												rate
												defaultRate
												startDate
												role {
													id
													name
												}
											}
										}
									}
								}
							}
						}
						roles(first: 10000) {
							edges {
								node {
									id
									name
								}
							}
						}
						expenseCategories(first: 10000) {
							edges {
								node {
									id
									name
									disabled
								}
							}
						}
					}
					project(id: $projectId) {
						...expensesPage_project
						...ratesAndInternalCostPage_project
						...projectBudgetV2_project @relay(mask: false)
					}
				}
			`,
		})
	)
);
